import { WidgetSkeleton, WidgetIssueState } from "@nexus/widget-sdk";
import { CHART_LEGEND_ICONS, EmptyDataState, useChartLifecycle } from "@nexus/widget-kit";
import { useEffect, useRef } from "react";
import * as echarts from "echarts/core";
import { PieChart, type PieSeriesOption } from "echarts/charts";
import {
  AriaComponent,
  LegendComponent,
  TooltipComponent,
  type AriaComponentOption,
  type LegendComponentOption,
  type TooltipComponentOption,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import {
  defineLabels,
  firstError,
  resolveLabels,
  resolveChartColors,
  unwrap,
  UpdatingPill,
  HeaderStats,
  Widget,
  WidgetState,
  WidgetSummary,
} from "@nexus/widget-sdk";
import type { ChartColors, WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import type {
  PirateSourceDisplayEntry,
  PirateSourceDistributionConfig,
  PirateSourceDistributionData,
  PirateSourceDistributionEntry,
  PirateSourceType,
} from "./types";
import { SOURCE_ORDER, sourceSliceColors } from "./palette";
import { PirateSourceSummary } from "./summary";

echarts.use([PieChart, AriaComponent, LegendComponent, TooltipComponent, CanvasRenderer]);

type PirateSourceOption = echarts.ComposeOption<
  PieSeriesOption | AriaComponentOption | LegendComponentOption | TooltipComponentOption
>;

export type {
  PirateSourceDisplayEntry,
  PirateSourceDistributionConfig,
  PirateSourceDistributionData,
  PirateSourceDistributionEntry,
  PirateSourceType,
} from "./types";
export { SOURCE_ORDER, sourceCssColor, sourceSliceColors } from "./palette";
export { PirateSourceSummary, rankPirateSources } from "./summary";

export const LABELS = defineLabels([
  { key: "sourceOtt", name: "OTT category name", default: "OTT" },
  { key: "sourceSat", name: "SAT category name", default: "SAT" },
  { key: "sourceOthers", name: "Others category name", default: "Others" },
  { key: "sourceUnknown", name: "Unknown category name", default: "Unknown" },
  { key: "totalCaption", name: "Total caption", default: "detections" },
  { key: "subtitle", name: "Subtitle", default: "Origin of detected content" },
  {
    key: "emptyTitle",
    name: "Empty state title",
    default: "No data available",
  },
  {
    key: "emptyDescription",
    name: "Empty state description",
    default: "No results for the current selection.",
  },
] as const);

type SourceLabels = Record<PirateSourceType, string>;

function sourceLabelsFrom(labels: Record<(typeof LABELS)[number]["key"], string>): SourceLabels {
  return {
    ott: labels.sourceOtt,
    sat: labels.sourceSat,
    others: labels.sourceOthers,
    unknown: labels.sourceUnknown,
  };
}

export const manifest: WidgetManifest = {
  id: "pirate-source-distribution",
  name: "Pirate source distribution",
  description: "Share of detections by the origin of the redistributed content.",
  version: "1.2.0",
  labels: LABELS,
  dataRequests: {
    distribution: {
      source: "pirate-source-distribution",
      contract: { name: "pirate-source-distribution", version: 1 },
      // Reused report-side (event-detail dashboard) with a dataBindings
      // override to "protection-overview-event-pirate-source-distribution"
      // (see domains.dashboards.builtins). A dataBindings entry only takes
      // effect when the request declares itself configurable — "false" here
      // silently produced "Data request does not allow a source override"
      // and no data ever loaded. The live pages that use the default source
      // (no dataBindings entry) are unaffected by this flag.
      configurable: true,
    },
  },
};

export const defaultConfig: PirateSourceDistributionConfig = {
  title: "Pirate source distribution",
};

const DEFAULT_SOURCE_LABELS: SourceLabels = {
  ott: "OTT",
  sat: "SAT",
  others: "Others",
  unknown: "Unknown",
};

export function buildPirateSourceDisplayEntries(
  entries: PirateSourceDistributionEntry[] = [],
  sourceLabels: SourceLabels = DEFAULT_SOURCE_LABELS,
): PirateSourceDisplayEntry[] {
  const counts = new Map(entries.map((entry) => [entry.source_type, Math.max(0, entry.count)]));
  const total = SOURCE_ORDER.reduce((sum, sourceType) => sum + (counts.get(sourceType) ?? 0), 0);
  const exact = SOURCE_ORDER.map((sourceType) =>
    total > 0 ? ((counts.get(sourceType) ?? 0) * 100) / total : 0,
  );
  const rounded = exact.map(Math.floor);
  let remainder = 100 - rounded.reduce((sum, value) => sum + value, 0);

  [...SOURCE_ORDER.keys()]
    .sort((left, right) => exact[right] - rounded[right] - (exact[left] - rounded[left]))
    .forEach((index) => {
      if (remainder > 0) {
        rounded[index] += 1;
        remainder -= 1;
      }
    });

  return SOURCE_ORDER.map((sourceType, index) => ({
    source_type: sourceType,
    label: sourceLabels[sourceType],
    count: counts.get(sourceType) ?? 0,
    total_count: total,
    percentage: exact[index],
    displayPercentage: total > 0 ? rounded[index] : 0,
  }));
}

function buildOption(entries: PirateSourceDisplayEntry[], colors: ChartColors): PirateSourceOption {
  return {
    animationDuration: 220,
    backgroundColor: "transparent",
    aria: { enabled: true },
    color: sourceSliceColors(colors.series),
    tooltip: {
      trigger: "item",
      appendTo: () => document.body,
      backgroundColor: colors.tooltipBg,
      borderColor: colors.axisLine,
      textStyle: { color: colors.tooltipText },
      formatter: "{b}: {d}%",
    },
    legend: {
      orient: "horizontal",
      left: "center",
      top: 10,
      icon: CHART_LEGEND_ICONS.rect,
      itemWidth: 6,
      itemHeight: 6,
      itemGap: 5,
      textStyle: { color: colors.label, fontSize: colors.typography?.labelSize ?? 12 },
    },
    series: [
      {
        type: "pie",
        radius: ["52%", "68%"],
        center: ["50%", "55%"],
        avoidLabelOverlap: true,
        itemStyle: { borderColor: colors.tooltipBg, borderWidth: 2 },
        label: { show: false },
        emphasis: { scaleSize: 4 },
        data: entries.map((entry) => ({
          name: entry.label,
          value: entry.count,
        })),
      },
    ],
  };
}

function PirateSourceDistributionComponent({
  data,
  config,
}: WidgetProps<PirateSourceDistributionConfig>) {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const entriesRef = useRef<PirateSourceDisplayEntry[]>([]);
  const error = firstError(data, ["distribution"]);
  const distribution = unwrap<PirateSourceDistributionData>(data.distribution);
  const isLoading = data.distribution?.isLoading ?? false;
  const labels = resolveLabels(LABELS, config);
  const entries = buildPirateSourceDisplayEntries(distribution?.entries, sourceLabelsFrom(labels));
  const total = entries[0]?.total_count ?? 0;
  const isEmpty = !isLoading && !error && total === 0;
  const showChart = !error && !isEmpty;

  useEffect(() => {
    entriesRef.current = entries;
    const host = chartRef.current;
    if (!host || error || isEmpty || isLoading) return;

    if (!chartInstanceRef.current || chartInstanceRef.current.isDisposed()) {
      chartInstanceRef.current = echarts.init(host, null, {
        renderer: "canvas",
      });
    }
    chartInstanceRef.current.setOption(buildOption(entries, resolveChartColors(host)), true);
  }, [entries, error, isEmpty, isLoading, config.presentation]);

  useChartLifecycle(
    chartRef,
    chartInstanceRef,
    (host, chart) => {
      chart.setOption(buildOption(entriesRef.current, resolveChartColors(host)), true);
    },
    config.presentation,
  );

  if (config.presentation === "summary") {
    return (
      <Widget
        title={config.title ?? defaultConfig.title}
        actions={isLoading && total > 0 ? <UpdatingPill /> : undefined}
      >
        {error ? (
          <WidgetIssueState error={error} />
        ) : isLoading && total === 0 ? (
          <WidgetSkeleton shape="share" />
        ) : isEmpty ? (
          <WidgetState title="No pirate source data for this selection" />
        ) : (
          <PirateSourceSummary entries={entries} labels={labels} />
        )}
      </Widget>
    );
  }

  return (
    <Widget
      title={config.title ?? defaultConfig.title}
      actions={isLoading && total > 0 ? <UpdatingPill /> : undefined}
      headerSummary={
        total > 0 ? (
          <HeaderStats
            label="Pirate source shares"
            items={entries.map((entry) => ({
              id: entry.source_type,
              label: entry.label,
              value: `${entry.displayPercentage}%`,
              format: "text",
            }))}
          />
        ) : undefined
      }
    >
      <div className="flex h-full min-h-0 flex-col">
        {!error && total > 0 ? (
          <WidgetSummary
            value={total.toLocaleString()}
            label={labels.totalCaption}
            description={labels.subtitle}
          />
        ) : null}
        <div className="relative min-h-0 w-full flex-1">
          <div
            ref={chartRef}
            className="absolute inset-0 h-full w-full transition-opacity duration-200"
            style={{ opacity: showChart ? 1 : 0 }}
            aria-label="Pirate source distribution"
          />

          {error && <WidgetIssueState error={error} className="absolute inset-0" />}
          {isEmpty && (
            <EmptyDataState
              title={labels.emptyTitle}
              description={labels.emptyDescription}
              className="absolute inset-0"
            />
          )}
          {isLoading && total === 0 && (
            <WidgetSkeleton shape="donut" className="absolute inset-0" />
          )}
        </div>
      </div>
    </Widget>
  );
}

export const Component = PirateSourceDistributionComponent;

const widgetModule: WidgetModule<PirateSourceDistributionConfig> = {
  manifest,
  Component,
  defaultConfig,
};

export default widgetModule;
